import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { gsap, ScrollTrigger } from 'gsap/all';
import { fullHeight, useHsHeader } from '../../util/hsHeader';
import FullScreenVideo from '../FullScreenVideo';
import VideoMobile from '../VideoMobile';

import ArtistHeading from '../ArtistHeading';
import {
  MobileBlockWrapper,
  AnimatedTextWrapper,
  ScrollForTheStory,
  centered,
} from '../../styles/helpers';
import { H3 } from '../../styles/typography';
import { useDeviceSize } from '../../util/useDeviceSize';
import { FadeInAnimation, PopupAnimation } from '../../effects/withAnimation';
import AudioPlayer, { AudioPlayerMobile } from '../AudioPlayer';

gsap.registerPlugin(ScrollTrigger);

const Wrapper = styled.div`
  ${fullHeight(5)}
  position: relative;
`;

export default function VideoSlide1({ video, copy, artist, audio }) {
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [isAudioFocused, setIsAudioFocused] = useState(false);
  const [{ headerHeight }] = useHsHeader();
  const { isWideScreen, isLongScreen } = useDeviceSize();

  const sliderRef = useRef();
  const openerRef = useRef();
  const copyRef = useRef();
  const audioRef = useRef();
  const videoRef = useRef();

  useEffect(() => {
    if (!isWideScreen) {
      return undefined;
    }

    const tl = gsap
      .timeline()
      .to(openerRef.current, {
        opacity: 0,
        duration: 0.2,
        ease: 'ease-in',
      })
      .add(() => {
        setIsVideoPlaying(v => !v);
      })
      .to(sliderRef.current, {
        duration: 0.4,
      })
      .add(() => {
        setIsVideoPlaying(v => !v);
      })
      .to(audioRef.current, {
        css: {
          pointerEvents: 'auto',
          opacity: 1,
        },
        duration: 0,
        delay: 0.1,
      })
      .add(() => {
        setIsAudioFocused(v => !v);
      })
      .to(audioRef.current, {
        css: {
          pointerEvents: 'none',
          opacity: 0,
        },
        duration: 0,
        delay: 0.3,
      })
      .add(() => {
        setIsAudioFocused(v => !v);
      })
      .to(copyRef.current, {
        opacity: 1,
        duration: 0.2,
      })
      .to(videoRef.current, {
        opacity: 0,
        duration: 0.2,
      });

    const st = ScrollTrigger.create({
      animation: tl,
      trigger: sliderRef.current,
      pin: true,
      pinSpacing: false,
      scrub: true,
      start: `top ${headerHeight}px`,
      end: 'bottom bottom',
    });

    return () => {
      tl.kill();
      st.kill();
    };
  }, [headerHeight, video, isWideScreen]);

  if (isWideScreen) {
    return (
      <Wrapper ref={sliderRef}>
        <FullScreenVideo
          videoId={video.id}
          pause={!isVideoPlaying}
          ref={videoRef}
        />

        <div
          css={css`
            position: absolute;
            width: 100%;
            ${fullHeight()}
            top: 0;
            left: 0;
            display: flex;
            flex-direction: column;
            pointer-events: none;
          `}
        >
          <ArtistHeading name={artist.name} occupation={artist.occupation} />

          <div
            css={css`
              display: flex;
              flex: 1;
              width: 100%;
              justify-content: space-between;
              align-items: center;
              flex-direction: column;
            `}
          >
            <div
              css={css`
                position: relative;
              `}
            >
              <H3
                ref={copyRef}
                css={css`
                  margin: ${isLongScreen ? '8vh' : '2vh'} 10vw;
                  max-width: 940px;
                  opacity: 0;
                `}
              >
                {copy[0].text}
              </H3>
              <div
                ref={audioRef}
                css={css`
                  position: absolute;
                  left: 0;
                  top: 0;
                  width: 100%;
                  height: 100%;
                  ${centered}
                  opacity: 0;
                  transition: opacity 0.2s ease-in;
                  pointer-events: none;
                `}
              >
                <AudioPlayer url={audio.url} isFocused={isAudioFocused} />
              </div>
            </div>
            <div ref={openerRef}>
              <ScrollForTheStory />
            </div>
          </div>
        </div>
      </Wrapper>
    );
  }

  return (
    <div>
      <div
        css={css`
          ${fullHeight()};
          flex-direction: column;
          justify-content: space-between;
          align-items: center;
          display: flex;
        `}
      >
        <ArtistHeading name={artist.name} occupation={artist.occupation} />

        <VideoMobile videoId={video.id} />

        <PopupAnimation minTopValue={0}>
          <ScrollForTheStory />
        </PopupAnimation>
      </div>
      <MobileBlockWrapper
        css={css`
          display: flex;
          justify-content: center;
        `}
      >
        <FadeInAnimation>
          <AudioPlayerMobile url={audio.url} />
        </FadeInAnimation>
      </MobileBlockWrapper>
      <AnimatedTextWrapper>
        <H3 mobile>{copy[0].text}</H3>
      </AnimatedTextWrapper>
    </div>
  );
}

VideoSlide1.propTypes = {
  video: PropTypes.shape({ id: PropTypes.number }),
  audio: PropTypes.shape({ url: PropTypes.string }),
  copy: PropTypes.arrayOf(PropTypes.shape({ text: PropTypes.node })),
  artist: PropTypes.shape({
    name: PropTypes.string,
    occupation: PropTypes.string,
  }),
};

VideoSlide1.defaultProps = {
  video: null,
  audio: null,
  copy: null,
  artist: null,
};
